/* ─────────────────────────────────────────────────────────────────────────
   The four industry hero products the core becomes — procedural stand-ins
   until dedicated .glb models exist. Each is ~0.8 units, origin at its
   footprint centre, facing +Z. Screens are live canvases.
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { PALETTE } from './enclosure';

function screen(w, h, px = 512) {
  const canvas = document.createElement('canvas');
  canvas.width = px; canvas.height = Math.round(px * h / w);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }));
  return { canvas, ctx: canvas.getContext('2d'), tex, mesh };
}
const rbox = (w, h, d, r, mat) => { const m = new THREE.Mesh(new RoundedBoxGeometry(w, h, d, 4, r), mat); m.castShadow = true; m.receiveShadow = true; return m; };
const ecg = (c, x0, y0, w, amp, t, color) => {
  c.strokeStyle = color; c.lineWidth = 3; c.beginPath();
  for (let x = 0; x <= w; x += 3) {
    const u = (x / w * 3 + t * 0.8) % 1;
    const spike = u > 0.42 && u < 0.5 ? Math.sin((u - 0.42) / 0.08 * Math.PI * 2) * amp : 0;
    const y = y0 - spike - Math.sin(u * Math.PI * 2) * amp * 0.08;
    x === 0 ? c.moveTo(x0 + x, y) : c.lineTo(x0 + x, y);
  }
  c.stroke();
};

function health() {
  const root = new THREE.Group();
  const shell = PALETTE.shell(), red = PALETTE.red(), glass = PALETTE.glass(), graphite = PALETTE.graphite();
  const body = rbox(0.6, 0.74, 0.2, 0.07, shell); body.position.y = 0.45; root.add(body);
  const foot = rbox(0.5, 0.08, 0.38, 0.035, shell); foot.position.set(0, 0.04, -0.02); root.add(foot);
  const neck = rbox(0.18, 0.12, 0.1, 0.03, graphite); neck.position.set(0, 0.1, -0.02); root.add(neck);
  const bez = rbox(0.5, 0.56, 0.02, 0.03, glass); bez.position.set(0, 0.48, 0.1); root.add(bez);
  const s = screen(0.46, 0.52); s.mesh.position.set(0, 0.48, 0.112); root.add(s.mesh);
  const btn = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.02, 20), red); btn.rotation.x = Math.PI / 2; btn.position.set(0.22, 0.16, 0.1); root.add(btn);
  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.02, 12, 30, Math.PI), graphite); handle.position.set(0, 0.82, 0); root.add(handle);
  const draw = t => {
    const c = s.ctx, W = s.canvas.width, H = s.canvas.height;
    c.fillStyle = '#07080b'; c.fillRect(0, 0, W, H);
    c.fillStyle = 'rgba(255,255,255,0.5)'; c.font = '600 20px "JetBrains Mono", monospace'; c.fillText('SpO₂ %', 30, 50);
    c.fillStyle = '#fff'; c.font = '800 190px Inter, sans-serif'; c.fillText('98', 24, 230);
    c.fillStyle = '#ff1a1a'; c.font = '700 40px Inter, sans-serif'; c.fillText('♥ ' + (72 + Math.round(Math.sin(t) * 2)), 330, 110);
    c.fillStyle = 'rgba(255,255,255,0.5)'; c.font = '600 18px "JetBrains Mono", monospace'; c.fillText('BPM', 350, 140);
    ecg(c, 20, 420, W - 40, 70, t, '#ff1a1a');
    c.fillStyle = 'rgba(255,255,255,0.08)'; c.fillRect(20, 480, W - 40, 60);
    c.fillStyle = '#19e36b'; c.font = '600 20px "JetBrains Mono", monospace'; c.fillText('● MONITORING · STABLE', 36, 518);
    s.tex.needsUpdate = true;
  };
  return { root, draw };
}

function gateway() {
  const root = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x17181c, roughness: 0.45, metalness: 0.4 });
  const red = PALETTE.red(), steel = PALETTE.steel(), graphite = PALETTE.graphite();
  const body = rbox(0.84, 0.26, 0.56, 0.05, dark); body.position.y = 0.13; root.add(body);
  for (let i = 0; i < 11; i++) { const f = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.05, 0.44), graphite); f.position.set(-0.32 + i * 0.064, 0.285, 0); f.castShadow = true; root.add(f); }
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.84 * 0.96, 0.014, 0.004), red); stripe.position.set(0, 0.2, 0.281); root.add(stripe);
  const leds = [];
  for (let i = 0; i < 5; i++) {
    const m = new THREE.MeshBasicMaterial({ color: i === 0 ? 0xff1a1a : 0x19e36b, toneMapped: false });
    const l = new THREE.Mesh(new THREE.CircleGeometry(0.01, 14), m); l.position.set(-0.34 + i * 0.04, 0.1, 0.282); root.add(l); leds.push(m);
  }
  for (let i = 0; i < 4; i++) { const p = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.05, 0.01), new THREE.MeshStandardMaterial({ color: 0x050506, roughness: 0.8 })); p.position.set(0.02 + i * 0.09, 0.1, 0.28); root.add(p); }
  [-0.28, 0, 0.28].forEach((x, i) => {
    const g = new THREE.Group(); g.position.set(x, 0.26, -0.24);
    const hinge = new THREE.Mesh(new THREE.SphereGeometry(0.03, 16, 12), steel); g.add(hinge);
    const ant = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.026, 0.52, 18), dark); ant.position.y = 0.27; ant.castShadow = true; g.add(ant);
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.022, 14, 10), dark); cap.position.y = 0.53; g.add(cap);
    g.rotation.z = (i - 1) * 0.18; g.rotation.x = -0.08;
    root.add(g);
  });
  const draw = t => leds.forEach((m, i) => { m.color.setHex(i === 0 ? 0xff1a1a : (Math.sin(t * 6 + i * 1.7) > 0 ? 0x19e36b : 0x0b3d20)); });
  return { root, draw };
}

function rover() {
  const root = new THREE.Group();
  const shell = PALETTE.shell(), red = PALETTE.red(), steel = PALETTE.steel(), graphite = PALETTE.graphite();
  const tire = new THREE.MeshStandardMaterial({ color: 0x111214, roughness: 0.9 });
  const chassis = rbox(0.66, 0.06, 0.46, 0.02, shell); chassis.position.y = 0.16; root.add(chassis);
  const deck = rbox(0.5, 0.03, 0.36, 0.012, red); deck.position.y = 0.2; root.add(deck);
  const pcb = rbox(0.3, 0.012, 0.22, 0.004, new THREE.MeshStandardMaterial({ color: 0x0e0f12, roughness: 0.5 })); pcb.position.set(-0.04, 0.24, 0); root.add(pcb);
  for (let i = 0; i < 6; i++) { const ch = new THREE.Mesh(new THREE.BoxGeometry(0.04 + (i % 2) * 0.03, 0.012, 0.04), graphite); ch.position.set(-0.14 + i * 0.05, 0.252, (i % 3 - 1) * 0.06); root.add(ch); }
  const batt = rbox(0.2, 0.07, 0.14, 0.01, graphite); batt.position.set(-0.17, 0.11, 0); root.add(batt);
  const wheels = [];
  [[-0.23, 0.26], [0.23, 0.26], [-0.23, -0.26], [0.23, -0.26]].forEach(([x, z]) => {
    const w = new THREE.Group(); w.position.set(x, 0.12, z);
    const t = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.08, 32), tire); t.rotation.x = Math.PI / 2; t.castShadow = true; w.add(t);
    const h = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.085, 20), red); h.rotation.x = Math.PI / 2; w.add(h);
    root.add(w); wheels.push(w);
  });
  const mast = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.18, 0.03), graphite); mast.position.set(0.24, 0.3, 0); root.add(mast);
  const head = rbox(0.06, 0.08, 0.26, 0.015, shell); head.position.set(0.26, 0.42, 0); root.add(head);
  [-0.065, 0.065].forEach(z => {
    const eye = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.05, 24), steel); eye.rotation.z = Math.PI / 2; eye.position.set(0.3, 0.42, z); root.add(eye);
    const iris = new THREE.Mesh(new THREE.CircleGeometry(0.03, 20), new THREE.MeshBasicMaterial({ color: 0x0a0a0a })); iris.rotation.y = Math.PI / 2; iris.position.set(0.3255, 0.42, z); root.add(iris);
  });
  root.rotation.y = -Math.PI / 2 + 0.5;
  const draw = t => wheels.forEach(w => { w.rotation.z = -t * 1.5; });
  return { root, draw };
}

function meter() {
  const root = new THREE.Group();
  const shell = new THREE.MeshPhysicalMaterial({ color: 0xdfe2e7, roughness: 0.5, clearcoat: 0.2 });
  const red = PALETTE.red(), graphite = PALETTE.graphite(), glass = PALETTE.glass();
  const body = rbox(0.52, 0.7, 0.2, 0.04, shell); body.position.y = 0.45; root.add(body);
  const term = rbox(0.52, 0.18, 0.22, 0.03, graphite); term.position.set(0, 0.09, 0.01); root.add(term);
  for (let i = 0; i < 6; i++) { const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.01, 12), PALETTE.steel()); sc.rotation.x = Math.PI / 2; sc.position.set(-0.19 + i * 0.076, 0.1, 0.125); root.add(sc); }
  const win = rbox(0.4, 0.22, 0.02, 0.02, glass); win.position.set(0, 0.62, 0.1); root.add(win);
  const s = screen(0.36, 0.18, 400); s.mesh.position.set(0, 0.62, 0.112); root.add(s.mesh);
  const band = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.012, 0.004), red); band.position.set(0, 0.46, 0.101); root.add(band);
  const pulse = new THREE.MeshBasicMaterial({ color: 0xff1a1a, toneMapped: false });
  const led = new THREE.Mesh(new THREE.CircleGeometry(0.014, 16), pulse); led.position.set(0.15, 0.38, 0.101); root.add(led);
  const port = new THREE.Mesh(new THREE.RingGeometry(0.02, 0.035, 24), new THREE.MeshStandardMaterial({ color: 0x222222 })); port.position.set(-0.13, 0.36, 0.101); root.add(port);
  const draw = t => {
    const c = s.ctx, W = s.canvas.width, H = s.canvas.height;
    c.fillStyle = '#0a0f0c'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#9dffc4'; c.font = '700 64px "JetBrains Mono", monospace';
    c.fillText((230 + Math.sin(t * 0.7) * 0.6).toFixed(1), 20, 90);
    c.font = '600 26px "JetBrains Mono", monospace'; c.fillText('V', 250, 90);
    c.fillStyle = 'rgba(157,255,196,0.6)'; c.font = '600 22px "JetBrains Mono", monospace';
    c.fillText((12.84 + t * 0.001).toFixed(3) + ' kWh', 20, 150); c.fillText('PF 0.98', 250, 150);
    s.tex.needsUpdate = true;
    pulse.color.setHex(Math.sin(t * 8) > 0.6 ? 0xff1a1a : 0x3a0505);
  };
  return { root, draw };
}

export const PRODUCTS = { health, gateway, rover, meter };
